import type { ReactNode } from 'react';

import type { HTMLAttributes } from 'types';

interface Props extends HTMLAttributes<HTMLElement> {
	/** List container for two or more items. */
	as?: 'ol' | 'ul';

	/** Container children, or the only output if container isn't active. */
	items: ReactNode[];

	/** Non-list container for a single item. */
	nonList?: 'div' | 'p';
}

/**
 * Render a list only if there are at least two items.
 *
 * If a list, the container is an ol or ul as specified and each item is
 * wrapped in an li tag. Otherwise the container is a div or p and the only
 * item rendered as its direct child.
 */
export default function OptionalList({
	as: List = 'ul',
	items,
	nonList: NonList = 'div',
	...containerProps
}: Props) {
	const isList = items.length > 1;
	const Tag = isList ? List : NonList;
	return (
		<Tag {...containerProps}>
			{isList
				? items.map((item, index) => (
						// Don't have anything else here.
						// oxlint-disable-next-line react/no-array-index-key
						<li key={index}>{item}</li>
					))
				: items[0]}
		</Tag>
	);
}
OptionalList.displayName = 'OptionalList';
